/**
 * Snowflake Cortex Agents Configuration
 *
 * For local development, we use the mock server on port 3001.
 * For production, set the environment variables to point to Snowflake.
 */

// Use mock server in development, real Snowflake in production
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development'

// Snowflake account URL
// Format: https://<account_identifier>.<region>.snowflakecomputing.com
// Example: https://xy12345.us-east-1.snowflakecomputing.com
export const SNOWFLAKE_ACCOUNT_URL = IS_DEVELOPMENT
  ? 'http://localhost:3001' // Mock server for local dev
  : process.env.REACT_APP_SNOWFLAKE_ACCOUNT_URL || 'https://YOUR_ACCOUNT.snowflakecomputing.com'

// API endpoint path
// Option 1: Generic agent endpoint (you pass config in request)
// '/api/v2/cortex/agent:run'
//
// Option 2: Pre-configured agent object endpoint
// '/api/v2/databases/{database}/schemas/{schema}/agents/{agent_name}:run'
export const SNOWFLAKE_AGENT_ENDPOINT = process.env.REACT_APP_SNOWFLAKE_AGENT_ENDPOINT || '/api/v2/cortex/agent:run'

// Token endpoint (if your BE provides a dedicated endpoint for Snowflake tokens)
export const SNOWFLAKE_TOKEN_ENDPOINT = process.env.REACT_APP_SNOWFLAKE_TOKEN_ENDPOINT || '/api/auth/snowflake-token'

/**
 * Get the Snowflake auth token
 *
 * Options:
 * 1. Dedicated endpoint that returns a Snowflake token
 * 2. Token from your existing auth context (Frontegg)
 * 3. Token stored in localStorage/sessionStorage after OAuth flow
 */
export async function getSnowflakeToken(): Promise<string> {
  // In development, return a mock token (mock server doesn't validate)
  if (IS_DEVELOPMENT) {
    return 'mock-dev-token'
  }

  // Option 1: Fetch from a dedicated endpoint
  // const response = await fetch(SNOWFLAKE_TOKEN_ENDPOINT)
  // if (!response.ok) {
  //   throw new Error('Failed to get Snowflake token')
  // }
  // const { token } = await response.json()
  // return token

  // Option 2: Get from existing auth context
  // import { useAuth } from '@frontegg/react'
  // return authContext.user?.accessToken

  // Option 3: Get from storage after OAuth flow
  // return localStorage.getItem('snowflake_token') || ''

  // Placeholder - throws error to remind you to implement
  throw new Error(
    'getSnowflakeToken() not implemented. ' + 'Please implement based on how your BE provides the Snowflake auth token.'
  )
}

/**
 * Feature flags for the chat UI
 */
export const CHAT_FEATURES = {
  // Show thinking/reasoning content from the agent
  showThinking: true,

  // Show tool calls and results
  showToolCalls: true,

  // Show agent status updates (Planning, Executing, etc.)
  showStatus: false,

  // Enable conversation history persistence
  persistHistory: false,

  // Max messages to keep in memory
  maxMessages: 100
}

/**
 * Status message mappings for better UX
 */
export const STATUS_MESSAGES: Record<string, string> = {
  planning: 'Planning the approach...',
  reasoning_agent_stop: 'Analyzing the results...',
  proceeding_to_answer: 'Forming the response...',
  executing: 'Executing...',
  default: 'Processing...'
}

export function getStatusMessage(status: string): string {
  return STATUS_MESSAGES[status] || STATUS_MESSAGES.default
}
